import styled from "styled-components";
import { theme } from "../../theme";
import { HomeIconSmall } from "../atoms/icons/HomeIcon";
import BodySmall from "../atoms/typography/BodySmall";
import { SearchListIcon } from "../atoms/icons/SearchListIcon";
import Divider from "../atoms/Divider";
import { UserIcon } from "../atoms/icons/UserIcon";
import { useLocation, useNavigate } from "react-router-dom";
import { PackageSearchIcon } from "../atoms/icons/PackageSearchIcon";
import { EditIcon } from "../atoms/icons/EditIcon";
import { FileIconOne, FilesIcon } from "../atoms/icons/FileIcons";
import { DatabaseIcon } from "../atoms/icons/DatabaseIcon";
import { StarIconSmall } from "../atoms/icons/StarIcon";
import { AddTeamIcon } from "../atoms/icons/AddTeamIcon";
import { UserListIcon } from "../atoms/icons/UserListIcon";
import { CheckListIcon } from "../atoms/icons/CheckListIcon";
import { WarningIcon } from "../atoms/icons/WarningIcon";
import { ChatSearchIcon } from "../atoms/icons/ChatSearchIcon";
import { LeftListIcon } from "../atoms/icons/LeftListIcon";
import { FireSecurityIcon } from "../atoms/icons/FireSecurityIcon";
import { SecurityValidationIcon } from "../atoms/icons/SecurityValidationIcon";
import { SettingsIconSmall } from "../atoms/icons/SettingsIcon";
import { useTranslation } from "react-i18next";
import { usePermissions } from "../../graphql/hooks/usePermissions";
import {
  AgreementsPermissions,
  AmlMonitoringPermissions,
  AmlRiskScorePermissions,
  BlacklistPermissions,
  MonitoringPermissions,
  OnboardingPermissions,
  RuleBucketsPermissions,
  SettingsPermissions,
  UsersPermissions,
  type AvailableReadPermission,
} from "../../utils/permissions";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import LoadingSpinner from "../atoms/LoadingSpinner";
import { SecurityLockIcon } from "../atoms/icons/SecurityLockIcon";
import {
  ChevronSmallDownIcon,
  ChevronSmallUpIcon,
} from "../atoms/icons/ChevronIcons";
import { SlideOutHeight } from "../atoms/SlideOutHeight";
import TextLinkButton from "../atoms/buttons/TextLinkButton";
import { HorizontalSpacer } from "../atoms/Spacers";
import { useAuth } from "../../auth/AuthProvider";
import { Row } from "react-grid-system";
import ExpandAndCollapse from "../atoms/buttons/ExpandAndCollapse";
import Small from "../atoms/typography/Small";
import { FilterCircleIcon } from "../atoms/icons/FilterCircleIcon";
import { DocumentValidationIcon } from "../atoms/icons/DocumentValidationIcon";
import { UserStatusIcon } from "../atoms/icons/UserStatusIcon";

const MenuContainer = styled.div`
  padding: 2rem 1rem;
  border-right: 2px solid ${theme.colors.textSecondary};
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 85vh;
  height: 100%;
  //background-color: ${theme.colors.white};
`;

const MenuItem = styled.button<{
  selected?: boolean;
  isSubItem?: boolean;
}>`
  border-radius: 4px;
  width: ${(props) => (props.isSubItem ? "216px" : "240px")};
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border: none;
  text-decoration: none;
  color: inherit;
  background-color: ${(props) =>
    props.selected ? theme.colors.primary : "transparent"};

  &:hover {
    background-color: ${theme.colors.grey05};
  }
`;
const SubMenuItem = styled.button<{ selected?: boolean }>`
  border-radius: 4px;
  width: 240px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  border: none;
  background-color: ${(props) =>
    props.selected ? theme.colors.primary : "transparent"};

  &:hover {
    background-color: ${theme.colors.grey05};
  }
`;

const CollapsedMenu = styled.div`
  padding: 4px 8px;
  border: 1px dashed ${theme.colors.textSecondary};
  border-radius: 12px;
  display: flex;
  gap: 16px;
  align-items: center;
  background-color: ${theme.colors.white};
  filter: opacity(0.8);
  position: absolute;
  top: -10px;
  left: 2px;
  box-shadow: ${theme.shadow.cardShadow};

  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.hover};
  }
`;

interface RoutePathsModel {
  url: string;
  label: string;
  icon: any;
  permissions?: Array<AvailableReadPermission>;
}

const oldSntPaths: Array<RoutePathsModel> = [
  { url: "/person", label: "menu.personCreditCheck", icon: SearchListIcon },
  { url: "/invoices-edit", label: "menu.workList", icon: EditIcon },
  { url: "/invoices", label: "menu.invoices", icon: FileIconOne },
  {
    url: "/tolkien",
    label: "menu.interpretationResults",
    icon: SearchListIcon,
  },
  {
    url: "/rule-buckets",
    label: "menu.rules",
    icon: DatabaseIcon,
    permissions: RuleBucketsPermissions,
  },
  { url: "/rating", label: "menu.rating", icon: StarIconSmall },
  { url: "/prospect", label: "menu.newCustomer", icon: AddTeamIcon },
  {
    url: "/onboardings",
    label: "menu.onboarding",
    icon: UserListIcon,
    permissions: OnboardingPermissions,
  },
  {
    url: "/agreements",
    label: "menu.agreements",
    icon: FileIconOne,
    permissions: AgreementsPermissions,
  },
  {
    url: "/monitoring-triggers",
    label: "menu.monitoringTriggers",
    icon: CheckListIcon,
    permissions: MonitoringPermissions,
  },
  {
    url: "/blacklist",
    label: "menu.blacklist",
    icon: WarningIcon,
    permissions: BlacklistPermissions,
  },
  { url: "/kyc", label: "menu.kyc", icon: ChatSearchIcon },
  { url: "/batchdetermination", label: "menu.batchRun", icon: LeftListIcon },
  {
    url: "/settings",
    label: "menu.settings",
    icon: SettingsIconSmall,
    permissions: SettingsPermissions,
  },
];

interface MfMenuItemsProps {
  label: string;
  icon: any;
  url?: string;
  permissions?: Array<AvailableReadPermission>;
  submenus?: Array<RoutePathsModel>;
}

const mfMenuItems: Array<MfMenuItemsProps> = [
  {
    label: "menu.documentHandler",
    url: "/document-handler",
    icon: PackageSearchIcon,
    permissions: [...AmlRiskScorePermissions, ...AmlMonitoringPermissions],
  },
  {
    label: "aml.aml",
    icon: SecurityLockIcon,
    submenus: [
      {
        url: "/aml-monitor-overview",
        label: "menu.amlMonitoringEvents",
        icon: FireSecurityIcon,
        permissions: AmlRiskScorePermissions,
      },
      {
        url: "/aml-risk-overview",
        label: "menu.amlRiskScores",
        icon: SecurityValidationIcon,
        permissions: AmlMonitoringPermissions,
      },
    ],
  },
  {
    label: "menu.registration",
    icon: FilterCircleIcon,
    submenus: [
      {
        url: "/document-handler",
        label: "menu.documentHandler",
        icon: PackageSearchIcon,
        permissions: [...AmlRiskScorePermissions, ...AmlMonitoringPermissions],
      },
      {
        url: "/invoices/validate",
        label: "menu.invoiceValidation",
        icon: DocumentValidationIcon,
      },
    ],
  },
  {
    label: "menu.invoices",
    icon: FilesIcon,
    url: "/invoices/all",
  },
  {
    label: "menu.users",
    icon: UserStatusIcon,
    url: "/users",
    permissions: UsersPermissions,
  },
];

const Menu: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { signoutRedirect } = useAuth();

  const currentPath = location.pathname;
  const [visiblePaths, setVisiblePaths] = useState<Array<RoutePathsModel>>([]);

  const [openSubMenus, setOpenSubMenus] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const update = () => {
      if (menuRef.current) {
        document.documentElement.style.setProperty(
          "--sidebar-width",
          `${menuRef.current.offsetWidth}px`,
        );
      }
    };
    update();
    const observer = new ResizeObserver(update);
    if (menuRef.current) observer.observe(menuRef.current);
    return () => observer.disconnect();
  }, [isExpanded]);

  const { permissions, error, loading } = usePermissions();

  const userPermissions = permissions?.availableForCurrentUser.readPermissions;

  useEffect(() => {
    if (userPermissions) {
      const availablePathsForUser = oldSntPaths.filter((path) => {
        // If no permissions required → always show
        if (!path.permissions) return true;

        // If required permissions → show only if user has at least one
        return path.permissions.some((requiredPermission) =>
          userPermissions?.includes(requiredPermission),
        );
      });
      setVisiblePaths(availablePathsForUser);
    }
  }, [permissions]);

  const hasAnyPermission = (
    requiredPermissions?: Array<AvailableReadPermission>,
  ) =>
    !requiredPermissions?.length ||
    requiredPermissions.some((requiredPermission) =>
      userPermissions?.includes(requiredPermission),
    );
  const visibleMfMenuItems = loading
    ? []
    : mfMenuItems.filter((menuItem) => hasAnyPermission(menuItem.permissions));

  return (
    <div ref={menuRef} style={{ position: "relative" }}>
      {!isExpanded && (
        <CollapsedMenu onClick={() => setIsExpanded(true)}>
          <Small fontweight={500}>{t("menu.expand")}</Small>
        </CollapsedMenu>
      )}
      {isExpanded && (
        <MenuContainer>
          <Row nogutter justify="end" style={{ marginTop: -16 }}>
            <ExpandAndCollapse
              isExpanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          </Row>
          <MenuItem
            as="a"
            href="/"
            selected={currentPath === "/"}
            onClick={(e: React.MouseEvent) => {
              if (!e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                navigate("/");
              }
            }}
          >
            <HomeIconSmall
              color={currentPath === "/" ? theme.colors.white : undefined}
            />
            {isExpanded && (
              <BodySmall
                color={currentPath === "/" ? theme.colors.white : undefined}
              >
                {t("menu.startPage")}
              </BodySmall>
            )}
          </MenuItem>

          <MenuItem
            as="a"
            href="/client"
            selected={currentPath.includes("client")}
            onClick={(e: React.MouseEvent) => {
              if (!e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                navigate("/client");
              }
            }}
          >
            <UserIcon
              color={
                currentPath.includes("client") ? theme.colors.white : undefined
              }
            />
            {isExpanded && (
              <BodySmall
                color={
                  currentPath.includes("client")
                    ? theme.colors.white
                    : undefined
                }
              >
                {t("menu.clients")}
              </BodySmall>
            )}
          </MenuItem>

          {loading ? (
            <LoadingSpinner />
          ) : (
            visiblePaths.map((path) => {
              const Icon = path.icon;
              const selected = currentPath === path.url;

              return (
                <MenuItem
                  as="a"
                  key={path.url}
                  href={path.url}
                  selected={selected}
                  onClick={(e: React.MouseEvent) => {
                    if (!e.metaKey && !e.ctrlKey) {
                      e.preventDefault();
                      navigate(path.url);
                    }
                  }}
                >
                  <Icon color={selected ? theme.colors.white : undefined} />
                  {isExpanded && (
                    <BodySmall
                      color={selected ? theme.colors.white : undefined}
                    >
                      {t(path.label)}
                    </BodySmall>
                  )}
                </MenuItem>
              );
            })
          )}

          {error && (
            <BodySmall color={theme.colors.error}>
              {t("errors.permissionsNotFound")}
            </BodySmall>
          )}

          {visibleMfMenuItems.map((menuItem) => {
            const Icon = menuItem.icon;

            return menuItem.submenus ? (
              <div key={menuItem.label}>
                <SubMenuItem
                  onClick={() => {
                    setOpenSubMenus((prev) => ({
                      ...prev,
                      [menuItem.label]: !prev[menuItem.label],
                    }));
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Icon />
                    {isExpanded && <BodySmall>{t(menuItem.label)}</BodySmall>}
                  </div>
                  {openSubMenus[menuItem.label] ? (
                    <ChevronSmallUpIcon />
                  ) : (
                    <ChevronSmallDownIcon />
                  )}
                </SubMenuItem>
                <SlideOutHeight isVisible={openSubMenus[menuItem.label]}>
                  <div
                    style={{
                      marginLeft: isExpanded ? 24 : 16,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    {menuItem.submenus.map((subMenu) => {
                      const SubIcon = subMenu.icon;

                      return (
                        <MenuItem
                          as="a"
                          key={subMenu.url}
                          href={subMenu.url}
                          selected={currentPath === subMenu.url}
                          isSubItem
                          onClick={(e: React.MouseEvent) => {
                            if (!e.metaKey && !e.ctrlKey) {
                              e.preventDefault();
                              navigate(subMenu.url!);
                            }
                          }}
                        >
                          <SubIcon
                            color={
                              currentPath === subMenu.url
                                ? theme.colors.white
                                : undefined
                            }
                          />
                          {isExpanded && (
                            <BodySmall
                              color={
                                currentPath === subMenu.url
                                  ? theme.colors.white
                                  : undefined
                              }
                            >
                              {t(subMenu.label)}
                            </BodySmall>
                          )}
                        </MenuItem>
                      );
                    })}{" "}
                  </div>
                </SlideOutHeight>
              </div>
            ) : (
              <MenuItem
                as="a"
                key={menuItem.url}
                href={menuItem.url}
                selected={currentPath === menuItem.url}
                onClick={(e: React.MouseEvent) => {
                  if (!e.metaKey && !e.ctrlKey) {
                    e.preventDefault();
                    navigate(menuItem.url!);
                  }
                }}
              >
                <Icon
                  color={
                    currentPath === menuItem.url
                      ? theme.colors.white
                      : undefined
                  }
                />
                {isExpanded && (
                  <BodySmall
                    color={
                      currentPath === menuItem.url
                        ? theme.colors.white
                        : undefined
                    }
                  >
                    {t(menuItem.label)}
                  </BodySmall>
                )}
              </MenuItem>
            );
          })}

          <Divider />
          <HorizontalSpacer spacing={0.5} />
          <TextLinkButton
            title="Logga ut"
            onClick={() => signoutRedirect()}
            color={theme.colors.textMuted}
          />
        </MenuContainer>
      )}
    </div>
  );
};

export default Menu;
